/**
 * Workspace state machine — the spine of the redesigned Atlas (doc 147 §5.3,
 * §5.4 rebuild). Two states drive the unified workspace:
 *
 *   idle     → no entity focused; graph is in ambient mode
 *   focused  → an entity is focused; graph dims non-neighbors, rail shows
 *              its summary and an "Open ⏎" CTA that navigates to the
 *              entity's full-page detail screen (`#entity/<id>`)
 *
 * The module is framework-free, pure, and fully unit-tested. The reducer
 * functions are the only way to transition between modes; the URL is a
 * one-to-one mirror of the mode so deep links restore the exact state.
 *
 * Path grammar (subordinate to the existing hash router's `workspace` route):
 *
 *   workspace          → idle
 *   workspace/Holding   → focused on Holding
 *
 * Legacy `workspace/Holding/sheet` and `workspace/Holding/sheet/<field>`
 * links (from the removed sheet overlay) are redirected to `#entity/<id>`
 * inside `hashRouter.ts`'s `parseHash`, before this module's parser ever
 * runs — so `parseWorkspacePath` never has to understand that grammar.
 */

import { MAX_ROUTE_ID_LENGTH } from './hashRouter.js';

export type WorkspaceMode =
  | { kind: 'idle' }
  | { kind: 'focused'; entityId: string };

export const initialWorkspaceMode: WorkspaceMode = { kind: 'idle' };

/* ── Predicates ──────────────────────────────────────────────────────────── */

export function isFocused(mode: WorkspaceMode): boolean {
  return mode.kind !== 'idle';
}

export function focusedEntityId(mode: WorkspaceMode): string | undefined {
  return mode.kind === 'idle' ? undefined : mode.entityId;
}

/* ── Transitions ─────────────────────────────────────────────────────────── */

/** Focus a new entity. */
export function focusEntity(_mode: WorkspaceMode, entityId: string): WorkspaceMode {
  return { kind: 'focused', entityId };
}

/** Clear focus — back to ambient idle from any state. */
export function clearFocus(_mode: WorkspaceMode): WorkspaceMode {
  return { kind: 'idle' };
}

/** Esc: step back one level — focused → idle. */
export function escape(mode: WorkspaceMode): WorkspaceMode {
  if (mode.kind === 'focused') return clearFocus(mode);
  return mode;
}

/**
 * Walk focus to a neighbor. No-op from idle (nothing to walk from).
 */
export function walkToNeighbor(mode: WorkspaceMode, neighborId: string): WorkspaceMode {
  if (mode.kind === 'idle') return mode;
  return focusEntity(mode, neighborId);
}

/** Alias for walkToNeighbor — used by mini-graph node clicks. */
export const swapFocus = walkToNeighbor;

/* ── URL serialization ───────────────────────────────────────────────────── */

/**
 * Serialize a workspace mode to its hash-path tail (no leading `workspace/`,
 * no leading slash).
 */
export function workspaceToPath(mode: WorkspaceMode): string {
  if (mode.kind === 'idle') return '';
  return encodeURIComponent(mode.entityId);
}

const withinBound = (s: string): boolean => s.length > 0 && s.length <= MAX_ROUTE_ID_LENGTH;

/** Parse a hash-path tail back into a workspace mode. Garbage falls back to idle. */
export function parseWorkspacePath(path: string): WorkspaceMode {
  const clean = path.trim();
  if (clean === '') return { kind: 'idle' };
  const rawId = clean.split('/')[0] ?? '';
  if (rawId === '') return { kind: 'idle' };
  let entityId: string;
  try {
    entityId = decodeURIComponent(rawId);
  } catch {
    return { kind: 'idle' };
  }
  if (!withinBound(entityId)) return { kind: 'idle' };
  // Any suffix beyond the id (unknown verb, or a legacy /sheet that slipped
  // through) is ignored — legacy /sheet links are intercepted in parseHash
  // before parseWorkspacePath ever runs.
  return { kind: 'focused', entityId };
}
